/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import _, { get } from 'lodash';
import {
    ICheckoutViewProps,
    ILineItem,
    ILineItemDeliveryGroup,
    ILineItems,
    IOrderSummary,
    IPickUpAtStore,
    IEmailDelivery,
    IInvoicePaymentSummary
} from '@msdyn365-commerce-modules/checkout';
import { ICheckoutProps as ICheckoutExtensionProps } from '../definition-extensions/checkout.ext.props.autogenerated';
import { AttributeTextValue, Cart, CartLine } from '@msdyn365-commerce/retail-proxy';
import { LinePagination } from './components/line-pagination';
import { IInvoiceSummaryLines, IInvoiceSummaryProps } from '@msdyn365-commerce-modules/invoice-payment-summary';
import { removeWarehouseAttributes } from '../../../utils/cartAttributeManager';
import { chargeLinesLogic } from '../../../utils/chargeLinesHelper';
import { IGetInvoiceAccountExtended } from '../definition-extensions/checkout.data';
import getGdnInvoices, { GetGdnInvoicesInput } from '../../../actions/get-gdn-invoices.action';
import { IQueryResultSettings } from '@msdyn365-commerce/retail-proxy';
import { IInvoiceTypesData, InvoiceTypesInvoiceType } from '../../../modules/gdn-invoices-list/gdn-invoices-list.props.autogenerated';
import { getAllowableFreightExclusionResult, IAllowableFreightExclusionMessages } from '../../../utils/cartlineFreightFlagHelper';
import getExtensionPropertyValueByKey, { CommercePropertyValueEnum } from '../../../utils/getExtensionPropertyValueByKey';

const PAYMENT_TYPE_EXTENSION_PROPERTY_NAME = 'SelectedPaymentType';

export interface IOrderSummaryExtended extends IOrderSummary {
    otherChargesLines: React.ReactNode[];
}

interface IInvoicepaymentSummaryExtented extends IInvoicePaymentSummary {
    otherChargesLines: React.ReactNode[];
}

export interface ILineItemsExtended extends ILineItems {
    numItems: number;
    prev: string;
    next: string;
    shippingState?: string;
    allowableFreightLtlFallbackMessage: string;
    allowableFreightOversizedFallbackMessage: string;
    allowableFreightOilFallbackMessage: string;
    allowableFreightExclusionFallbackMessage: string;
    cartLineByLineId: Map<string | undefined, CartLine>;
    configMessages: IAllowableFreightExclusionMessages | undefined;
    enableAllowableFreightExclusionMessages: boolean | undefined;
}

export interface ILineItemDeliveryGroupExtended extends ILineItemDeliveryGroup {
    numItems: number;
    prev: string;
    next: string;
    shippingState?: string;
    allowableFreightLtlFallbackMessage: string;
    allowableFreightOversizedFallbackMessage: string;
    allowableFreightOilFallbackMessage: string;
    allowableFreightExclusionFallbackMessage: string;
    cartLineByLineId: Map<string | undefined, CartLine>;
    configMessages: IAllowableFreightExclusionMessages | undefined;
    enableAllowableFreightExclusionMessages: boolean | undefined;
}

interface ILineItemExtended extends ILineItem {
    shippingState?: string;
    allowableFreightLtlFallbackMessage: string;
    allowableFreightOversizedFallbackMessage: string;
    allowableFreightOilFallbackMessage: string;
    allowableFreightExclusionFallbackMessage: string;
    cartLine: CartLine | undefined;
    configMessages: IAllowableFreightExclusionMessages | undefined;
    enableAllowableFreightExclusionMessages: boolean | undefined;
}

export const PickUpAtStoreComponent: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const EmailDeliveryComponent: React.FC<IEmailDelivery> = ({ EmailDelivery, label }) => <Node {...EmailDelivery}>{label}</Node>;

export const LineItemComponent: React.FC<ILineItemExtended> = ({
    LineItem,
    item,
    pickUpAtStore,
    emailDelivery,
    shippingState,
    allowableFreightLtlFallbackMessage,
    allowableFreightOversizedFallbackMessage,
    allowableFreightOilFallbackMessage,
    allowableFreightExclusionFallbackMessage,
    cartLine,
    configMessages,
    enableAllowableFreightExclusionMessages
}) => {
    const allowableFreightExclusionResult = getAllowableFreightExclusionResult(
        cartLine,
        {
            allowableFreightLtlFallbackMessage,
            allowableFreightOversizedFallbackMessage,
            allowableFreightOilFallbackMessage,
            allowableFreightExclusionFallbackMessage
        },
        configMessages,
        enableAllowableFreightExclusionMessages ?? false
    );

    // Clone the item ReactNode and inject the checkoutShippingState prop
    const clonedItem =
        item && React.isValidElement(item)
            ? React.cloneElement(item as React.ReactElement, {
                  checkoutShippingState: shippingState,
                  allowableFreightLtlFallbackMessage,
                  allowableFreightOversizedFallbackMessage,
                  allowableFreightOilFallbackMessage,
                  allowableFreightExclusionFallbackMessage,
                  allowableFreightExclusionResult
              })
            : item;

    return (
        <Node {...LineItem}>
            {clonedItem}
            {pickUpAtStore && <PickUpAtStoreComponent {...pickUpAtStore} />}
            {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
        </Node>
    );
};

//
export const LineItemGroupComponent: React.FC<ILineItemDeliveryGroupExtended> = ({
    LineItemDeliveryGroup,
    LineItemList,
    heading,
    lineItems,
    numItems,
    prev,
    next,
    shippingState,
    allowableFreightLtlFallbackMessage,
    allowableFreightOversizedFallbackMessage,
    allowableFreightOilFallbackMessage,
    allowableFreightExclusionFallbackMessage,
    cartLineByLineId,
    configMessages,
    enableAllowableFreightExclusionMessages
}) => {
    if (lineItems && lineItems.length > 0 && !lineItems[0].item) {
        return null;
    }

    const defaultPageLength: number = 5;
    const pageLength = numItems || defaultPageLength;
    const [paginatedView, setPaginatedView] = React.useState<ILineItem[]>(
        lineItems && lineItems.length >= pageLength ? lineItems.slice(0, pageLength) : lineItems
    );
    const [paginationPage, setPaginationPage] = React.useState<number>(0);

    const nextPage = () => {
        changePage(paginationPage + 1);
    };

    const prevPage = () => {
        changePage(paginationPage - 1);
    };

    const changePage = (page: number) => {
        if (page < 0) {
            return;
        }

        if (lineItems && page > lineItems.length / pageLength) {
            return;
        }

        let skip = page * pageLength;
        if (lineItems && lineItems.length >= skip) {
            setPaginationPage(page);
            let slice = lineItems?.slice(skip);
            setPaginatedView(slice.length >= pageLength ? slice.slice(0, pageLength) : slice);
        }
    };

    const pagination = () => {
        return (
            <LinePagination
                nextPage={nextPage}
                prevPage={prevPage}
                changePage={changePage}
                currentPage={paginationPage}
                numPages={lineItems.length ? Math.ceil(lineItems.length / pageLength) : 0}
                prev={prev}
                next={next}
            />
        );
    };

    if (paginatedView.length === 0 || !paginatedView[0].item) {
        return null;
    }

    return (
        <Node {...LineItemDeliveryGroup}>
            {heading}
            <Node {...LineItemList}>
                {/* {lineItems.map(lineItem => ( */}
                {paginatedView.map(lineItem => {
                    const cartLine = cartLineByLineId.get(lineItem.LineId);
                    return (
                        <LineItemComponent
                            key={lineItem.LineId}
                            {...lineItem}
                            shippingState={shippingState}
                            allowableFreightLtlFallbackMessage={allowableFreightLtlFallbackMessage}
                            allowableFreightExclusionFallbackMessage={allowableFreightExclusionFallbackMessage}
                            allowableFreightOilFallbackMessage={allowableFreightOilFallbackMessage}
                            allowableFreightOversizedFallbackMessage={allowableFreightOversizedFallbackMessage}
                            configMessages={configMessages}
                            enableAllowableFreightExclusionMessages={enableAllowableFreightExclusionMessages}
                            cartLine={cartLine}
                        />
                    );
                })}
            </Node>
            {pagination()}
        </Node>
    );
};

export const LineItemGroupComponentWithMultiplePickUp: React.FC<ILineItemDeliveryGroup> = ({
    LineItemDeliveryGroup,
    LineItemList,
    heading,
    lineItems,
    lineItemWraper,
    lineItemWraperIcon
}) => (
    <Node {...LineItemDeliveryGroup}>
        {lineItemWraperIcon}
        {lineItemWraper}
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponentWithMultiplePickUp key={lineItem.LineId} {...lineItem} />
            ))}
        </Node>
    </Node>
);

export const LineItemComponentWithMultiplePickUp: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore, emailDelivery }) => (
    <Node {...LineItem}>
        {item}
        {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
    </Node>
);

export const PickUpAtStoreComponentWithMultiplePickUp: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const LineItemsComponent: React.FC<ILineItemsExtended> = ({
    LineItems,
    Header,
    heading,
    editLink,
    itemsForPickup,
    itemsForShip,
    itemsForEmail,
    itemsGroupWithMulitplePickupMode,
    numItems,
    prev,
    next,
    shippingState,
    allowableFreightLtlFallbackMessage,
    allowableFreightOversizedFallbackMessage,
    allowableFreightOilFallbackMessage,
    allowableFreightExclusionFallbackMessage,
    cartLineByLineId,
    configMessages,
    enableAllowableFreightExclusionMessages
}) => {
    const items = itemsForPickup ? itemsForPickup : itemsForEmail ? itemsForEmail : itemsForShip;
    if (!items) {
        return null;
    }
    const lineItemGroupComponentExt: ILineItemDeliveryGroupExtended = {
        ...items!,
        numItems,
        prev,
        next,
        shippingState,
        allowableFreightLtlFallbackMessage,
        allowableFreightOversizedFallbackMessage,
        allowableFreightOilFallbackMessage,
        allowableFreightExclusionFallbackMessage,
        cartLineByLineId,
        configMessages,
        enableAllowableFreightExclusionMessages
    };

    return (
        <Node {...LineItems}>
            <Node {...Header}>
                {heading}
                {editLink}
            </Node>
            {itemsGroupWithMulitplePickupMode === undefined && itemsForPickup && <LineItemGroupComponent {...lineItemGroupComponentExt} />}
            {itemsGroupWithMulitplePickupMode === undefined && itemsForEmail && <LineItemGroupComponent {...lineItemGroupComponentExt} />}
            {itemsGroupWithMulitplePickupMode === undefined && itemsForShip && <LineItemGroupComponent {...lineItemGroupComponentExt} />}
            {itemsGroupWithMulitplePickupMode !== undefined
                ? itemsGroupWithMulitplePickupMode.map((item, index) => {
                      return <LineItemGroupComponentWithMultiplePickUp {...item} key={index} />;
                  })
                : null}
        </Node>
    );
};

const OrderSummaryComponent: React.FC<IOrderSummaryExtended> = ({ heading, lines, otherChargesLines }) => {
    const shippingLine = lines?.shipping;
    if (shippingLine) {
        _.set(shippingLine as object, 'props.freeText', 'To Be Calculated');
    }

    return (
        <div className='msc-order-summary-wrapper'>
            {heading}
            <div className='msc-order-summary__items'>
                {lines && (
                    <>
                        {lines.subtotal}
                        {shippingLine}
                        {/* {lines.otherCharge} */}
                        {otherChargesLines && otherChargesLines}
                        {lines.tax}
                        {/* {lines.totalDiscounts} */}
                        {lines.loyalty}
                        {lines.giftCard}
                        {lines.orderTotal}
                    </>
                )}
            </div>
        </div>
    );
};

/**
 * Render invoice summary line.
 */
const renderInvoiceSummaryLine = (label: string | undefined, amount: string, type: string) => {
    return (
        <p className={`msc-invoice-summary__line-${type}`}>
            <span className='msc-invoice-summary__label'>{label}</span>
            <span className='msc-price msc-invoice-summary__value'>
                <span className='msc-price__actual' itemProp='price'>
                    {amount}
                </span>
            </span>
        </p>
    );
};

/**
 * Custom InvoiceSummary component.
 */
const InvoiceSummary = (props: IInvoiceSummaryProps, invoiceCssLabel: string, totalCssLabel: string) => {
    // const { TotalAmount, CartLines } = props.cart;
    const { CartLines, TotalAmount } = props.cart;
    const invoiceLines = CartLines?.filter(cartLine => cartLine.IsInvoiceLine);
    const total = TotalAmount ? props.context.cultureFormatter.formatCurrency(TotalAmount) : 'N/A';
    const reactNodes: IInvoiceSummaryLines = {
        invoices: invoiceLines?.map((invoiceLine: CartLine) => {
            const amount = invoiceLine.ExtendedPrice ? props.context.cultureFormatter.formatCurrency(invoiceLine.ExtendedPrice) : 'N/A';
            return renderInvoiceSummaryLine(invoiceLine.InvoiceId, amount, invoiceCssLabel);
        }),
        orderTotal: renderInvoiceSummaryLine(props.orderTotalLabel, total, totalCssLabel)
    };
    return reactNodes;
};

const getInvoicePaymentSummary = (input: ICheckoutViewProps & ICheckoutExtensionProps<{}>): IInvoicePaymentSummary => {
    const {
        data: { checkout },
        resources: { summaryTotalAmountLabel, invoiceLabel, invoiceSummaryTitle },
        context,
        typeName,
        id,
        telemetry
    } = input;

    const checkoutCart = get(checkout, 'result.checkoutCart');
    // const cart = get(checkout, 'result.checkoutCart.cart');
    const cart: Cart = checkout.result?.checkoutCart.cart!;
    // cart?.CartLines?.forEach(cartLine => cartLine.IsInvoiceLine && (cartLine.InvoiceAmount = cartLine.ExtendedPrice));

    return {
        heading: <h2 className='msc-invoice-summary__heading'>{invoiceSummaryTitle}</h2>,
        lines: checkoutCart?.hasInvoiceLine
            ? InvoiceSummary(
                  {
                      orderTotalLabel: summaryTotalAmountLabel,
                      invoiceLabel,
                      cart,
                      context,
                      typeName,
                      telemetry,
                      id,
                      checkoutState: checkout.result
                  },
                  input.resources.invoiceCssLabel,
                  input.resources.totalCssLabel
              )
            : undefined
    };
};

const PaymentSummaryComponent: React.FC<IInvoicepaymentSummaryExtented> = ({ heading, lines, otherChargesLines }) => (
    <div className='msc-invoice-summary-wrapper'>
        {heading}
        <div className='msc-invoice-summary__items'>
            {lines && (
                <>
                    {lines.invoices}
                    {lines.giftCard}
                    {otherChargesLines && otherChargesLines}
                    {lines.loyalty}
                    {lines.orderTotal}
                </>
            )}
        </div>
    </div>
);

const _renderShipOnCheckout = (
    shipCompleteOnCheckout: boolean,
    handleShipCompleteOnCheckoutChange: (e: React.MouseEvent<HTMLInputElement, MouseEvent>) => void,
    shipCompleteOnCheckoutLabel: string
): React.ReactNode => (
    <div className='ms-checkout__ship-on-checkout'>
        <label className='input-group-prepend print-view-toggle__label'>
            <span className={'print-view-toggle__heading'}>{shipCompleteOnCheckoutLabel}</span>
            <input
                value={shipCompleteOnCheckout ? 1 : 0}
                onClick={handleShipCompleteOnCheckoutChange}
                type='checkbox'
                className='print-view-toggle__checkbox'
            />
            <div className={`print-view-toggle__toggle print-view-toggle__toggle--${shipCompleteOnCheckout ? 'checked' : 'unchecked'}`} />
        </label>
    </div>
);

const _renderOrderComment = (commentHeading: string, commentString: string): React.ReactNode => {
    const emptyStringClassName = commentString == '';
    return (
        <div className={`order-level-comment-wrapper ${emptyStringClassName ? 'order-level-comment-wrapper--empty-string' : ''}`}>
            <h1 className='order-level-comment-wrapper__heading'>{commentHeading}</h1>
            <p className='order-level-comment-wrapper__text'>{commentString}</p>
        </div>
    );
};

const _renderLineChargeNode = (chargeName: string, chargeAmount: string): React.ReactNode => {
    return (
        <p className='msc-order-summary__line-other-charges'>
            <span className='msc-order-charge-line__label'>{chargeName}</span>{' '}
            <span className='msc-price msc-order-summary__value'>{chargeAmount}</span>
        </p>
    );
};

const PlaceOrderButtonWithMessage: React.FC<{
    placeOrderButton: React.ReactNode;
    isButtonDisabled: boolean;
    shouldDisplay: boolean;
    message: string;
}> = ({ placeOrderButton, isButtonDisabled, shouldDisplay, message }) => (
    <div style={{ display: 'inline-block', width: '100%' }}>
        {placeOrderButton}
        {isButtonDisabled && shouldDisplay && <p className='ms-checkout__validation-message'>{message}</p>}
    </div>
);

const CheckoutView: React.FC<ICheckoutViewProps & ICheckoutExtensionProps<IGetInvoiceAccountExtended>> = props => {
    const attributeArr = props.data.checkout.result?.checkoutCart.cart.AttributeValues || [];
    const getShipCompleteIndex = (): number | undefined => {
        return props.data.checkout.result?.checkoutCart.cart.AttributeValues?.findIndex(attribute => attribute.Name === 'ShipComplete');
    };
    // Get the text in the order level comment, if it doesn't exist, assign an empty string
    const getCommentText = (): string => {
        return props.data.checkout.result?.checkoutCart.cart.Comment || '';
    };
    // Get the comment heading text from the resources
    const getCommentHeadingText = (): string => {
        return props.resources.commentHeadingText;
    };
    const getShipCompleteValue = (): boolean => {
        const shipCompleteIndex = getShipCompleteIndex();
        if (shipCompleteIndex === -1 || shipCompleteIndex === undefined) {
            return false;
        } else {
            const attr = attributeArr[shipCompleteIndex] as AttributeTextValue;
            return attr?.TextValue === 'true';
        }
    };
    const handleShipCompleteOnCheckoutChange = () => {
        const shipComplete = {
            '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
            Name: 'ShipComplete',
            // TextValue: (!getShipCompleteValue()).toString()
            TextValue: (!shipCompleteOnCheckout).toString()
        };
        const idx = getShipCompleteIndex();
        idx === undefined || idx === -1 ? attributeArr.push(shipComplete) : attributeArr.splice(idx, 1, shipComplete);
        const cart: Cart = Object.assign({}, props.data.checkout.result?.checkoutCart.cart);
        if (cart) {
            cart.AttributeValues = attributeArr;
            saveShipCompleteToCart(cart);
        }
    };
    const saveShipCompleteToCart = async (cart: Cart) => {
        if (cart) {
            const checkoutState = props.data.checkout.result;
            if (!checkoutState) {
                return;
            }

            await checkoutState.checkoutCart.updateCart({ newCartObject: cart }).then(() => {
                // setShipCompleteOnCheckout(getShipCompleteValue());
                setShipCompleteOnCheckout(!shipCompleteOnCheckout);
            });
        }
    };
    const [shipCompleteOnCheckout, setShipCompleteOnCheckout] = React.useState<boolean>(getShipCompleteValue());
    const [invoiceValidationError, setInvoiceValidationError] = React.useState<string | undefined>(undefined);
    const shipCompleteOnCheckoutToggle = _renderShipOnCheckout(
        shipCompleteOnCheckout,
        handleShipCompleteOnCheckoutChange,
        props.resources.shipCompleteOnCheckoutLabel
    );

    const clearSelectedPaymentTypeOnCart = (cart: Cart): boolean => {
        const extensionProperties = [...(cart.ExtensionProperties || [])];

        const selectedPaymentTypeIndex = extensionProperties.findIndex(property => property.Key === PAYMENT_TYPE_EXTENSION_PROPERTY_NAME);

        if (selectedPaymentTypeIndex === -1) {
            return false;
        }

        const currentValue = extensionProperties[selectedPaymentTypeIndex].Value?.BooleanValue;

        if (currentValue === false) {
            return false;
        }

        extensionProperties[selectedPaymentTypeIndex] = {
            ...extensionProperties[selectedPaymentTypeIndex],
            Value: {
                ...extensionProperties[selectedPaymentTypeIndex].Value,
                BooleanValue: false
            }
        };

        cart.ExtensionProperties = extensionProperties;
        return true;
    };

    const getAllowableFreightAttIdx = () => {
        return props.data.checkout.result?.checkoutCart.cart.AttributeValues?.findIndex(
            attribute => attribute.Name === 'AllowableFreightAmount'
        );
    };

    const getInvoiceAmountDueAttIdx = () => {
        return props.data.checkout.result?.checkoutCart.cart.AttributeValues?.findIndex(attribute => attribute.Name === 'InvoiceAmountDue');
    };

    React.useEffect(() => {
        const updateCartOnPageLoad = async (): Promise<void> => {
            const checkoutState = props.data.checkout.result;
            const checkoutCart = checkoutState?.checkoutCart;

            if (!checkoutCart) {
                return;
            }

            const cart: Cart = { ...checkoutCart.cart };
            let shouldRefreshCart = false;

            const shouldUpdateExtensionProperties = clearSelectedPaymentTypeOnCart(cart);
            const isInvoiceLine = checkoutCart.hasInvoiceLine;

            if (isInvoiceLine) {
                const cartLines: CartLine[] | undefined = cart?.CartLines;

                if (cartLines) {
                    const attributeArr: AttributeTextValue[] = [...(cart.AttributeValues || [])];
                    const allowableFreightIdx = getAllowableFreightAttIdx();

                    if (allowableFreightIdx && allowableFreightIdx !== -1) {
                        try {
                            let freightData: { invoiceId: string; amount: number }[] = JSON.parse(
                                attributeArr[allowableFreightIdx].TextValue || '[]'
                            );

                            const invoiceAmountDueIdx = getInvoiceAmountDueAttIdx();

                            let invoiceAmountDueData: { invoiceId: string; amountDue: number }[] = [];

                            if (invoiceAmountDueIdx !== -1 && invoiceAmountDueIdx !== undefined) {
                                try {
                                    invoiceAmountDueData = JSON.parse(attributeArr[invoiceAmountDueIdx].TextValue || '[]');
                                } catch {
                                    invoiceAmountDueData = [];
                                }
                            }

                            const updatedFreightData = freightData.filter(item => {
                                const cartLine = cartLines.find(line => line.InvoiceId === item.invoiceId);
                                const invoiceAmountDue = invoiceAmountDueData.find(data => data.invoiceId === item.invoiceId)?.amountDue;
                                const shouldRemoveAllowableAmount =
                                    !cartLine ||
                                    (cartLine.ExtendedPrice && invoiceAmountDue && cartLine.ExtendedPrice < invoiceAmountDue - item.amount);

                                return !shouldRemoveAllowableAmount;
                            });

                            if (updatedFreightData.length !== freightData.length) {
                                attributeArr[allowableFreightIdx] = {
                                    '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
                                    Name: 'AllowableFreightAmount',
                                    ExtensionProperties: [],
                                    TextValue: JSON.stringify(updatedFreightData),
                                    TextValueTranslations: []
                                } as AttributeTextValue;

                                cart.AttributeValues = attributeArr;
                            }

                            await checkoutCart.updateAttributeValues({ newAttributeValues: attributeArr });
                            shouldRefreshCart = true;
                        } catch (error) {
                            console.error('Error parsing AllowableFreightAmount:', error);
                        }
                    }
                }
            }

            if (shouldUpdateExtensionProperties && cart.ExtensionProperties) {
                await checkoutCart.updateExtensionProperties({
                    newExtensionProperties: cart.ExtensionProperties
                });

                shouldRefreshCart = true;
            }

            if (shouldRefreshCart) {
                await checkoutCart.refreshCart({});
            }
        };

        void updateCartOnPageLoad();
    }, []);

    // List of customer account numbers that are authorized to use the alternate warehouse feature.
    const authorizedWarehouseAccounts: string[] = props.context.app.config.authorizedWarehouseAccounts || [];

    // Configuration flag to enable or disable the alternate warehouse feature.
    const isAlternateWarehouseFeatureEnabled: boolean = props.context.app.config.enableAlternateWarehouseFeature ?? false;

    React.useEffect(() => {
        if (props.data.checkout?.result?.checkoutCart.cart.CartLines) {
            removeWarehouseAttributes(
                props.data.checkout.result.checkoutCart,
                authorizedWarehouseAccounts,
                isAlternateWarehouseFeatureEnabled,
                props.context.request.user.customerAccountNumber
            );
        }
    }, [props.data.checkout?.result?.checkoutCart.cart.CartLines, isAlternateWarehouseFeatureEnabled, authorizedWarehouseAccounts]);

    const _renderAccountLockedMessage = (): React.ReactNode => (
        <div className='account-lock-alert'>{props.config.accountLockedMessage}</div>
    );

    const _renderInvoiceValidationError = (): React.ReactNode => (
        <div className='invoice-validation-error' role='alert' aria-live='assertive'>
            {invoiceValidationError}
        </div>
    );

    const {
        canShow,
        checkoutProps,
        headerProps,
        hasSalesOrder,
        hasInvoiceLine,
        bodyProps,
        mainProps,
        mainControlProps,
        sideProps,
        sideControlFirstProps,
        sideControlSecondProps,
        termsAndConditionsProps,
        orderConfirmation,
        loading,
        alert,
        title,
        guidedForm,
        orderSummary,
        invoicePaymentSummary,
        lineItems,
        placeOrderButton,
        termsAndConditions,
        keepShoppingButton,
        resources: {
            allowableFreightLtlFallbackMessage,
            allowableFreightExclusionFallbackMessage,
            allowableFreightOilFallbackMessage,
            allowableFreightOversizedFallbackMessage
        },
        context
    } = props;
    const defaultPageLength: number = 5;
    const pageLength = props.config.pageLength || defaultPageLength;
    const cart: Cart = Object.assign({}, props.data.checkout.result?.checkoutCart.cart);

    const cartChargeLines = cart?.ChargeLines || [];
    const cartLinesChargeLines = (cart?.CartLines?.map(item => Array.from(item.ChargeLines || [])) || []).flat();
    const allChargeLines = [...cartChargeLines, ...cartLinesChargeLines];
    let chargelines = chargeLinesLogic(
        props.context.app.config.chargeLinesBreakdown,
        props.context.cultureFormatter.formatCurrency,
        allChargeLines,
        _renderLineChargeNode,
        cart.OtherChargeAmount || 0
    );
    const orderSummaryExtended: IOrderSummaryExtended = { ...orderSummary!, otherChargesLines: chargelines };

    const invoiceAccountData = props.data.invoiceAccountInfo.result?.Blocked || false;
    const isAccountBlocked: boolean | undefined = props.data.customerInformation.result?.Blocked;
    const isBlocked = invoiceAccountData || isAccountBlocked;
    const shippingState = props.data.checkout.result?.shippingAddress?.State;

    const cartLines = cart.CartLines;
    const configMessages: IAllowableFreightExclusionMessages | undefined = context.app.config.allowableFreightExclusionMessages;
    const enableAllowableFreightExclusionMessages: boolean = context.app.config.enableAllowableFreightExclusionMessages ?? false;

    const cartLineByLineId = React.useMemo(() => {
        return new Map(cartLines?.map(line => [line.LineId, line]) ?? []);
    }, [cartLines]);

    const lineItemsExt: ILineItemsExtended = {
        ...lineItems!,
        numItems: pageLength,
        prev: props.resources.paginationPrev || '',
        next: props.resources.paginationNext || '',
        shippingState: shippingState,
        allowableFreightLtlFallbackMessage,
        allowableFreightExclusionFallbackMessage,
        allowableFreightOilFallbackMessage,
        allowableFreightOversizedFallbackMessage,
        cartLineByLineId,
        configMessages,
        enableAllowableFreightExclusionMessages
    };
    const invoicePaymentSummaryProps = getInvoicePaymentSummary(props);
    const invoicePaymentSummaryExtended: IInvoicepaymentSummaryExtented = { ...invoicePaymentSummaryProps, otherChargesLines: chargelines };

    // logic used to change timeout message - ticket 13673
    let isInvoiceTimeout = false;
    let alertMessage: string | undefined;

    if (React.isValidElement(alert)) {
        alertMessage = alert.props?.message;
        isInvoiceTimeout = alertMessage === props.resources.invoiceCheckoutApiFailureErrorMessage;
    }
    const validateInvoiceBalances = React.useCallback(async () => {
        if (!props.config.enableInvoiceBalanceValidation || !hasInvoiceLine) {
            setInvoiceValidationError(undefined);
            return;
        }
        const checkoutState = props.data.checkout.result;

        if (!checkoutState) {
            return;
        }

        const invoicePayments = checkoutState?.checkoutCart.cart.CartLines ?? [];
        const invoiceIds: string[] = invoicePayments.map(line => line.InvoiceId).filter((id): id is string => id !== undefined);

        try {
            const searchCriteria = {
                InvoiceIds: invoiceIds
            };
            const queryResultSettings: IQueryResultSettings = {
                Paging: {
                    Top: invoiceIds.length,
                    Skip: 0
                }
            };
            const invoiceTypes: IInvoiceTypesData[] = [{ invoiceType: InvoiceTypesInvoiceType.None }];

            const invoices = await getGdnInvoices(
                new GetGdnInvoicesInput(searchCriteria, queryResultSettings, invoiceTypes),
                props.context.actionContext
            );
            const missingInvoices = invoicePayments.filter(cartLine => !invoices.find(invoice => invoice.Id === cartLine.InvoiceId));
            const insufficientBalanceInvoices = invoicePayments.filter(cartLine => {
                const matchedInvoice = invoices.find(invoice => invoice.Id === cartLine.InvoiceId);
                if (!matchedInvoice) {
                    return false;
                }
                return (cartLine.TotalAmount ?? 0) > (matchedInvoice.AmountBalance ?? 0);
            });
            if (insufficientBalanceInvoices.length > 0 || missingInvoices.length > 0) {
                setInvoiceValidationError(
                    props.config.invoiceBalanceValidationErrorMessage || props.resources.invoiceBalanceValidationErrorMessage
                );
                return;
            }

            setInvoiceValidationError(undefined);
        } catch (error) {
            console.error('Error validating invoices:', error);
            setInvoiceValidationError('Unable to validate invoice balances. Please try again.');
        }
    }, [props.data.checkout.result?.checkoutCart.cart.CartLines]);

    React.useEffect(() => {
        validateInvoiceBalances();
    }, [validateInvoiceBalances]);

    const hasSelectedPaymentMethod = React.useMemo(() => {
        return (
            (getExtensionPropertyValueByKey(
                cart.ExtensionProperties,
                PAYMENT_TYPE_EXTENSION_PROPERTY_NAME,
                CommercePropertyValueEnum.boolean
            ) as boolean) ?? false
        );
    }, [cart.ExtensionProperties]);

    const canPlaceOrder = _.get(placeOrderButton, 'props.canPlaceOrder', false);
    const shouldDisablePlaceOrderForMissingPayment = !hasSelectedPaymentMethod;
    const isButtonDisabled = !canPlaceOrder || shouldDisablePlaceOrderForMissingPayment;

    const shouldDisplayOrderValidationMessage = props.config.shouldDisplayOrderValidationMessage ?? false;
    const placeOrderValidationMessage = props.config.placeOrderValidationMessage || props.resources.placeOrderValidationMessageText;

    const disabledPlaceOrderButton = (
        <button className='ms-checkout__btn-place-order msc-btn btn-primary' disabled>
            {hasInvoiceLine ? props.resources.confirmPaymentText : props.resources.placeOrderText}
        </button>
    );

    const validatedOrderButton =
        invoiceValidationError || shouldDisablePlaceOrderForMissingPayment ? disabledPlaceOrderButton : placeOrderButton;

    const customAlert = isInvoiceTimeout ? (
        <div className='ms-checkout__info-message--invoice-timeout' role='alert' aria-live='polite'>
            {props.resources.invoiceCheckoutApiFailureErrorMessage}
        </div>
    ) : (
        alert
    );
    return (
        <Module {...checkoutProps}>
            {!hasSalesOrder && <Node {...headerProps}>{title}</Node>}
            {!hasSalesOrder && (
                <Node {...bodyProps}>
                    {loading}
                    {isBlocked ? _renderAccountLockedMessage() : null}
                    {invoiceValidationError && _renderInvoiceValidationError()}
                    {customAlert}
                    {canShow && (
                        <>
                            <Node {...mainProps}>
                                {guidedForm}
                                <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                {!hasInvoiceLine && props.config.shipCompleteOnCheckout && shipCompleteOnCheckoutToggle}
                                <Node {...mainControlProps}>
                                    <PlaceOrderButtonWithMessage
                                        placeOrderButton={validatedOrderButton}
                                        isButtonDisabled={isButtonDisabled}
                                        shouldDisplay={shouldDisplayOrderValidationMessage}
                                        message={placeOrderValidationMessage}
                                    />
                                    {keepShoppingButton}
                                </Node>
                            </Node>
                            <Node {...sideProps}>
                                {!hasInvoiceLine
                                    ? orderSummary && <OrderSummaryComponent {...orderSummaryExtended} />
                                    : invoicePaymentSummary && <PaymentSummaryComponent {...invoicePaymentSummaryExtended} />}
                                <Node {...sideControlFirstProps}>
                                    <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                    <PlaceOrderButtonWithMessage
                                        placeOrderButton={validatedOrderButton}
                                        isButtonDisabled={isButtonDisabled}
                                        shouldDisplay={shouldDisplayOrderValidationMessage}
                                        message={placeOrderValidationMessage}
                                    />
                                    {keepShoppingButton}
                                </Node>
                                {lineItems && <LineItemsComponent {...lineItemsExt} />}
                                {_renderOrderComment(getCommentHeadingText(), getCommentText())}
                                <Node {...sideControlSecondProps}>
                                    <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                    <PlaceOrderButtonWithMessage
                                        placeOrderButton={validatedOrderButton}
                                        isButtonDisabled={isButtonDisabled}
                                        shouldDisplay={shouldDisplayOrderValidationMessage}
                                        message={placeOrderValidationMessage}
                                    />
                                    {keepShoppingButton}
                                </Node>
                            </Node>
                        </>
                    )}
                </Node>
            )}
            {hasSalesOrder && orderConfirmation}
        </Module>
    );
};

export default CheckoutView;
